import { Injectable } from '@angular/core';
import { readSupportSession } from '@modules/auth/models/support-session.model';
import { UserService } from '@modules/auth/services/user.service';
import { combineLatest, Observable, race, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';

import { AnalyticsService } from './analytics.service';

/**
 * Feature flag de PostHog que habilita el onboarding a Facturación Digital (`/fiscal`, la píldora del
 * Home y el ítem «Fiscal» del menú). Si no existe, `getExperimentVariant` devuelve false y todo queda
 * oculto (fail-closed). NO depende del plan fiscal: el onboarding es el paso previo a la activación.
 *
 * La clave conserva el nombre `fiscal-recaudos` de FINA-2669: el módulo viejo de recaudos se retiró
 * (FINA-3666), pero renombrarla obligaría a recrear el flag en los dos proyectos de PostHog.
 */
export const FISCAL_ONBOARDING_FLAG = 'fiscal-recaudos';

/** Tope de espera del gate del guard de ruta hasta que carguen los flags de PostHog (~1.5 s). */
const FLAG_WAIT_MS = 3000;

/**
 * Acceso al onboarding fiscal = flag de PostHog **y** dueño del comercio (decisión de negocio, 2026-11-03:
 * todo el proceso es solo del dueño; un usuario asociado no ve el menú, la píldora ni `/fiscal`, y el api le
 * responde 403). Una sesión de soporte de Fina también entra, en modo lectura (el api no la deja editar).
 *
 * Vive en `app-common` porque lo consumen el side-nav y el Home, que no pueden importar del módulo lazy
 * `fiscal-onboarding`. Patrón: `AssistantAccessService` (dueño + condición).
 */
@Injectable({ providedIn: 'root' })
export class FiscalOnboardingFlagService {
    constructor(private readonly analyticsService: AnalyticsService, private readonly userService: UserService) {}

    /** Lectura síncrona (fail-closed): flag ON y sesión del dueño o de soporte. Para `*ngIf`, el menú y la carga del estado. */
    canAccessSync(): boolean {
        return this.flagOn() && this.ownerOrSupport();
    }

    /**
     * Emite el estado del gate cuando cambia el flag o la sesión. Los flags de PostHog llegan hasta ~1.5 s
     * después del init, así que un consumidor debe re-evaluar en `flagsUpdated$`. Arranca emitiendo
     * el valor actual.
     */
    canAccessChanges$(): Observable<boolean> {
        return combineLatest([this.analyticsService.flagsUpdated$.pipe(startWith(undefined)), this.userService.user$]).pipe(
            map(() => this.canAccessSync()),
            distinctUntilChanged(),
        );
    }

    /**
     * Versión asíncrona para el guard de ruta: permite apenas el flag esté ON (y la sesión sea del dueño o
     * de soporte); si los flags no cargan a tiempo (`FLAG_WAIT_MS`), decide con el valor actual
     * (fail-closed → deniega).
     */
    canAccess$(): Observable<boolean> {
        return race(this.canAccessChanges$().pipe(filter(Boolean), take(1)), timer(FLAG_WAIT_MS).pipe(map(() => this.canAccessSync())));
    }

    private flagOn(): boolean {
        return this.analyticsService.getExperimentVariant(FISCAL_ONBOARDING_FLAG);
    }

    private ownerOrSupport(): boolean {
        return !!this.userService.user$.value?.isOwner || this.isSupportSession();
    }

    /** Sesión de soporte de Fina (impersonación): puede mirar el expediente, nunca editarlo. Método aparte para poder simularlo en los specs. */
    protected isSupportSession(): boolean {
        return !!readSupportSession();
    }
}
